import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from "react";

// Tema claro/escuro.
//
// Comportamento: sem escolha salva, segue o sistema — e continua seguindo se o
// sistema mudar (quem usa troca automática ao anoitecer não quer ficar preso no
// tema da manhã). No primeiro clique a escolha passa a ser explícita e para de
// acompanhar o sistema.
//
// O tema mora em `data-theme` no <html>, não em contexto React, porque o CSS é
// quem aplica; o contexto existe só para o botão e para o Mermaid, que precisa
// re-renderizar os diagramas.

export type Theme = "light" | "dark";

interface ThemeValue {
  theme: Theme;
  isDark: boolean;
  setTheme: (next: Theme) => void;
  toggle: () => void;
  clearPreference: () => void;
  followsSystem: boolean;
}

const STORAGE_KEY = "lt_docs_theme";
const ThemeContext = createContext<ThemeValue | null>(null);

const systemPrefersDark = (): boolean =>
  typeof matchMedia === "function" && matchMedia("(prefers-color-scheme: dark)").matches;

function readStored(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    // localStorage bloqueado (modo privado, cookies negados): sem preferência
    // salva o site continua funcionando, só sempre segue o sistema.
    return null;
  }
}

function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.dataset.theme = theme;
  // Faz o navegador desenhar barras de rolagem e controles nativos no tom certo.
  root.style.colorScheme = theme;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [explicit, setExplicit] = useState<Theme | null>(readStored);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  const theme = explicit || (systemDark ? "dark" : "light");

  // O <html> já vem com data-theme do script inline no index.html; isto mantém
  // em sincronia depois de qualquer troca.
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Só acompanha o sistema enquanto não houver escolha explícita.
  useEffect(() => {
    if (explicit || typeof matchMedia !== "function") return undefined;
    const mq = matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [explicit]);

  const setTheme = useCallback((next: Theme) => {
    setExplicit(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {}
  }, []);

  const toggle = useCallback(() => {
    setTheme(document.documentElement.dataset.theme === "dark" ? "light" : "dark");
  }, [setTheme]);

  // Voltar a seguir o sistema — não tem botão hoje, mas o provider oferece.
  const clearPreference = useCallback(() => {
    setExplicit(null);
    setSystemDark(systemPrefersDark());
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  return (
    <ThemeContext.Provider
      value={{ theme, isDark: theme === "dark", setTheme, toggle, clearPreference, followsSystem: !explicit }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme precisa estar dentro de <ThemeProvider>.");
  return ctx;
}

export default useTheme;
